// React Query provider for API response caching across the application.
import React, { FC, PropsWithChildren } from 'react';
import { QueryCache, QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { captureException as shouldCaptureException } from 'utils/fnError';

const reportQueryError = (error: unknown) => {
    if (shouldCaptureException(error as AxiosError)) {
        Sentry.captureException(error);
    }
};

export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: reportQueryError,
    }),
    mutationCache: new MutationCache({
        onError: reportQueryError,
    }),
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 30 * 60 * 1000, // 30 minutes
            refetchOnWindowFocus: false,
            retry: 0,
        },
    },
});

export const clearQueryCache = () => {
    queryClient.clear();
};

export const QueryProvider: FC<PropsWithChildren> = ({ children }) => (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
);
